import {
  FilterValueDTO,
  IFragmentFragmentScreenDTO,
  ProteinFormulationDTO,
  WellIngDTO,
} from 'business/models/Models';
import { useEffect, useMemo } from 'react';
import {
  GetProteinFormulation,
  getIngredientByIngIngTypeId,
  getIngredientStock,
  getNewId,
} from 'utils/Global';
import {
  IWellDropsData,
  useLatestSelectedSearchRow,
  useSearchDisplayFields,
  useSearchFilters,
  useSearchResult,
  useWellDropsWithIngredients,
} from 'redux/hooks/searchnFilter';
import { fieldsMap } from './utils';
import { IIngredientProteinInfo } from 'components/commandpanel/IngredientInfo/IngredientProteinInfo';
import { LayerIngredient } from 'business/models/Layer';
import { IStockRowProps } from 'components/commandpanel/RequiredStock/RequiredStock';
import {
  transformIIngredientProteinInfo,
  transformIIngredientWellInfoProps,
} from 'transformers/searchnFilter';
import { useRMDispatch } from 'redux/hooks';
import { resetSearchFilterValues } from 'redux/reducers/searchnFilterReducer';

const getIngredients = (ings): IStockRowProps[] => {
  if (!ings) {
    return [];
  }
  return ings.map((ing) => {
    const gIng = getIngredientByIngIngTypeId(ing.ingredientIngredientTypeId);
    const gIngStock = getIngredientStock(ing.ingredientStockId);
    return {
      ingredientStockId: ing.ingredientStockId,
      name: gIng.longName,
      stocknum: gIngStock?.stockNumber,
      ph: gIngStock?.stockPh,
      volume: ing.volume,
      vendorpartnum: gIngStock?.vendorPartNumber,
    } as IStockRowProps;
  });
};

const getLayerIngredient = (ings): LayerIngredient[] => {
  if (!ings) {
    return [];
  }
  return ings.map((ing) => {
    const gIngStock = getIngredientStock(ing.ingredientStockId);
    return {
      ingredientColor: ing.hexColor,
      concentration: ing.concentration,
      concentrationUnit: ing.concentrationUnit,
      ingredientVolume: ing.volume,
      ingredientTypeName: ing.ingredientTypeName,
      ingredientStock: gIngStock,
      dropNumber: null,
      ingredientName: ing.ingredientName,
    } as LayerIngredient;
  });
};

export const useWellAndDropDetails = (): {
  wellProps: any;
  proteinProps: any;
  additivesProps: any;
} | null => {
  const latestSelectedRow = useLatestSelectedSearchRow();
  const wells_ = useWellDropsWithIngredients();

  return useMemo(() => {
    if (!latestSelectedRow || !wells_) {
      return null;
    }

    const selectedWells = wells_.filter(
      (well: WellIngDTO) => well.wellId === latestSelectedRow[fieldsMap.wellId],
    );
    if (selectedWells.length === 0) {
      return null;
    }

    const drops = selectedWells
      .map((w) => w.dropList.filter((d) => d.dropId !== 0))
      .flat();
    const wellLayers = selectedWells[0].wellLayerList;
    const selectedDrop = drops.filter(
      (drop) => drop.dropId === latestSelectedRow[fieldsMap.wellDropId],
    )[0];
    if (!selectedDrop) {
      return null;
    }

    let dropIngredients = [];
    let dropProteinVolume = 0,
      dropWellVolume = 0;
    let wellLayerIngredients = [];

    const dropsWellData: IWellDropsData = {
      wellVolume: selectedDrop.dropWellVolume,
      dropProteinVolume: selectedDrop.dropProteinVolume,
      seedId: selectedDrop.seedID,
      dropNumber: selectedDrop.dropNumber,
    };

    selectedDrop.dropLayerList.forEach((dropLayer) => {
      dropIngredients = [...dropIngredients, ...dropLayer.dropLayerIngredientList];
    });

    drops.forEach((drop) => {
      dropProteinVolume += drop.dropProteinVolume;
      dropWellVolume += drop.dropWellVolume;
    });

    const dropVolumes = { dropProteinVolume, dropWellVolume };

    wellLayers.forEach((wellLayer) => {
      wellLayerIngredients = [
        ...wellLayerIngredients,
        ...wellLayer.wellLayerIngredientList,
      ];
    });

    const wellName = `${selectedWells[0].rowLetter}${selectedWells[0].columnNumber}`;
    const wellVolume = selectedWells[0].wellVolume;
    const wellWaterVolume = selectedWells[0].wellWaterVolume;
    const wellNameVolume = { wellName, wellVolume, wellWaterVolume };

    const additivesProps = {
      additives: getIngredients(dropIngredients),
      dropNumber: latestSelectedRow[fieldsMap.dropNumber],
    };

    const pfInfo: ProteinFormulationDTO = GetProteinFormulation(
      latestSelectedRow[fieldsMap.proteinFormulationId],
    );

    const fragmentIds = selectedDrop.dropLayerList?.flatMap((dll) =>
      dll.dropLayerFragmentList?.flatMap((dlf) => {
        return {
          fragmentId: dlf.fragmentId,
          fragmentScreenId: selectedDrop.fragmentScreenId,
        } as IFragmentFragmentScreenDTO;
      }),
    );
    const proteinProps: IIngredientProteinInfo = transformIIngredientProteinInfo(
      false,
      pfInfo,
      dropsWellData,
      selectedDrop.seedID,
    );
    proteinProps.fragmentIds = fragmentIds;
    const wellLayerIngs = getLayerIngredient(wellLayerIngredients);

    wellLayerIngs?.forEach((wellLayerIng) => {
      wellLayerIng.dropNumber = latestSelectedRow[fieldsMap.dropNumber];
    });
    const wellProps = transformIIngredientWellInfoProps(
      wellName,
      false,
      dropVolumes,
      wellLayerIngs,
      wellNameVolume,
    );

    return { proteinProps, wellProps, additivesProps };
  }, [latestSelectedRow, wells_]);
};

export const usePrimaryFilterEffect = () => {
  const displayFields = useSearchDisplayFields();
  const filters = useSearchFilters();
  const searchResult = useSearchResult();
  const { primaryFilter: primaryFilterId } = filters;
  const dispatch = useRMDispatch();
  const primaryFilter = filters.data.find((f) => f.id === primaryFilterId);

  useEffect(() => {
    if (primaryFilter) {
      const displayField = displayFields.find(
        (df) => df.searchFieldId === primaryFilter.searchFieldId,
      );
      const filteredResult = searchResult.rows.items.filter((row) =>
        primaryFilter.values
          .map((fv) => fv.value)
          .includes(row[displayField.columnHeader]?.toString()),
      );
      const restFilters = filters.data.filter((f) => f.id !== primaryFilterId);
      restFilters.forEach((filter) => {
        const displayField = displayFields.find(
          (df) => df.searchFieldId === filter.searchFieldId,
        );
        const values: FilterValueDTO[] = [];
        filteredResult.forEach((row) => {
          if (!values.find((fv) => fv.value == row[displayField.columnHeader])) {
            values.push({
              id: getNewId(),
              filterId: filter.id,
              value: row[displayField.columnHeader]?.toString(),
            } as FilterValueDTO);
          }
        });
        dispatch(resetSearchFilterValues({ filterId: filter.id, values }));
      });
    }
  }, [primaryFilter?.values]);
};
